import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntake } from "../lib/intake-context-value";
import { API_BASE } from "../lib/api";
import { readAttribution } from "../lib/attribution";

// Anti-robocall gate: outbound autonomous Voice AI calls are off the table
// (FCC 2024 AI-voice ruling, state two-party consent laws). Qualified leads
// schedule a callback from a HUMAN study coordinator inside a chosen window.
//
// Parity note: these ids/labels MUST stay identical to CONTACT_WINDOWS in
// apps/agent/src/types.ts — the server validates the id against that
// allowlist. Kept client-side (rather than a shared package) because the
// intake bundle must not depend on the worker package's internals; a golden
// parity test in apps/agent/tests/intake-callback-gate.test.ts fails CI if
// the two ever drift.
const CALLBACK_WINDOWS = [
  { id: "morning", label: "Morning (9:00 AM – 12:00 PM)" },
  { id: "afternoon", label: "Afternoon (1:00 PM – 4:00 PM)" },
  { id: "evening", label: "Evening (5:00 PM – 7:00 PM)" },
] as const;

type CallbackWindowId = (typeof CALLBACK_WINDOWS)[number]["id"];

const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
];

/**
 * Detect the device timezone exactly once per mount. No silent fallback:
 * if detection fails (or yields nothing), the select stays on its explicit
 * placeholder and the patient must choose — never defaulted to
 * America/New_York, which would attest a timezone that may be false.
 */
function detectTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

interface CallbackPreferenceSectionProps {
  detectedTimezone: string | null;
  timezoneOptions: readonly string[];
  callbackWindow: CallbackWindowId | "";
  onCallbackWindowChange: (w: CallbackWindowId | "") => void;
  timezone: string;
  onTimezoneChange: (tz: string) => void;
  timezoneConfirmed: boolean;
  onTimezoneConfirmChange: (confirmed: boolean) => void;
  tcpaConsent: boolean;
  onTcpaConsentChange: (consent: boolean) => void;
}

/**
 * The TCPA callback-preference fieldset. Completable only when a window is
 * chosen, the timezone is selected AND confirmed, and explicit consent is
 * checked. Pure presentation — all state lives in the page.
 */
function CallbackPreferenceSection({
  detectedTimezone,
  timezoneOptions,
  callbackWindow,
  onCallbackWindowChange,
  timezone,
  onTimezoneChange,
  timezoneConfirmed,
  onTimezoneConfirmChange,
  tcpaConsent,
  onTcpaConsentChange,
}: CallbackPreferenceSectionProps) {
  return (
    <fieldset className="rounded-xl border border-gray-200 p-4 space-y-4">
      <legend className="px-1 text-[13px] font-medium text-gray-700">
        When should the study coordinator call you?
      </legend>

      <div className="space-y-2">
        <label htmlFor="callback-window" className="block text-[13px] font-medium text-gray-700">
          Preferred callback window
        </label>
        <select
          id="callback-window"
          value={callbackWindow}
          onChange={(e) => onCallbackWindowChange(e.target.value as CallbackWindowId | "")}
          className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)]"
          aria-required="true"
        >
          <option value="" disabled>
            Select a time window…
          </option>
          {CALLBACK_WINDOWS.map((w) => (
            <option key={w.id} value={w.id}>
              {w.label}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <label htmlFor="callback-timezone" className="block text-[13px] font-medium text-gray-700">
          Your timezone
        </label>
        <p className="text-[12px] text-gray-500">
          {detectedTimezone
            ? (
              <>
                Detected from your device: <span className="font-medium text-gray-700">{detectedTimezone}</span>
              </>
            )
            : "We could not detect your timezone — please choose it below."}
        </p>
        <select
          id="callback-timezone"
          value={timezone}
          onChange={(e) => onTimezoneChange(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] bg-white focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)]"
          aria-required="true"
        >
          <option value="" disabled>
            Select your timezone…
          </option>
          {timezoneOptions.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={timezoneConfirmed}
            onChange={(e) => onTimezoneConfirmChange(e.target.checked)}
            className="w-5 h-5 mt-0.5 rounded border-gray-300"
          />
          <span className="text-[13px] text-gray-600">
            This timezone is correct.
          </span>
        </label>
      </div>

      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={tcpaConsent}
          onChange={(e) => onTcpaConsentChange(e.target.checked)}
          className="w-5 h-5 mt-0.5 rounded border-gray-300"
          aria-required="true"
        />
        <span className="text-[13px] text-gray-600">
          I consent to receiving a call or SMS from a clinical study coordinator at this number regarding my trial eligibility.
        </span>
      </label>

      <p className="text-[12px] text-gray-500">
        Calls are made by a human study coordinator during your chosen window — never an automated or AI-generated voice system.
      </p>
    </fieldset>
  );
}

export default function QualifiedPage() {
  const navigate = useNavigate();
  const { token, prescreenResult, completeResult, setCompleteResult } = useIntake();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [zip, setZip] = useState("");
  // Future-trial contact is opt-IN and defaults OFF — the checkbox is never
  // pre-checked. The server persists opt_in_future_trials only on a literal
  // true, so a silent default here would enroll people in contact they never
  // chose. (Nothing else on this form is defaulted to a passing value either:
  // callback window, timezone confirmation and TCPA consent all start empty.)
  const [optIn, setOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Callback preference — all three must be satisfied before submit.
  // The timezone select starts on an explicit placeholder (no silent
  // America/New_York default): the patient must actually choose.
  const [callbackWindow, setCallbackWindow] = useState<CallbackWindowId | "">("");
  const [timezone, setTimezone] = useState("");
  // Single confirmation atom: the checkbox attests to ONE confirmed value.
  // Checked-ness is derived (confirmedTimezone === timezone), so changing
  // the select automatically un-confirms the old value — no special-case
  // reset in the onChange handler.
  const [confirmedTimezone, setConfirmedTimezone] = useState<string | null>(null);
  const [tcpaConsent, setTcpaConsent] = useState(false);

  // Detect once per mount, not on every render.
  const detectedTimezone = useMemo(detectTimezone, []);
  const timezoneOptions = useMemo(
    () =>
      detectedTimezone && !COMMON_TIMEZONES.includes(detectedTimezone)
        ? [...COMMON_TIMEZONES, detectedTimezone].sort()
        : COMMON_TIMEZONES,
    [detectedTimezone]
  );
  const timezoneConfirmed = confirmedTimezone !== null && confirmedTimezone === timezone;
  const callbackComplete = callbackWindow !== "" && timezone !== "" && timezoneConfirmed && tcpaConsent;

  // Play 2 — segment-aware copy. Prefer the worker's authoritative
  // classifier in prescreenResult; fall back to the URL hint from
  // PrescreenPage if state was lost (e.g. hard reload mid-flow).
  const segment =
    prescreenResult?.segment ??
    (new URLSearchParams(window.location.search).get("seg") === "glp1rec"
      ? "glp1_recovery"
      : "standard");
  const isGlp1Recovery = segment === "glp1_recovery";

  const handleSubmit = async () => {
    setError(null);
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    // Fail-closed client gate (the server re-validates): no callback window,
    // no confirmed timezone, or no explicit consent → no coordinator callback.
    if (!callbackComplete) {
      setError("Please choose a callback window, confirm your timezone, and check the consent box.");
      return;
    }
    setLoading(true);
    try {
      const attribution = readAttribution();
      const res = await fetch(`${API_BASE}/intake/vesper5/complete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          phone: phone || null,
          zip: zip || null,
          optInFutureTrials: optIn,
          // Human-coordinator callback preference (TCPA anti-robocall gate).
          // The server stamps the consent timestamp at submission time.
          callbackPreference: {
            preferred_contact_window: callbackWindow,
            contact_timezone: timezone,
            tcpa_consent: tcpaConsent,
          },
          // Re-emit the welcome-page UTMs + segment hint so the worker
          // can stamp the registry row with the original source.
          ...(attribution && { attribution }),
        }),
      });
      const json = await res.json();
      if (!json.success) {
        setError(json.error || "Completion failed.");
        return;
      }
      setCompleteResult(json.data);
    } catch (e) { console.error("[intake] request failed", e);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const done = !!completeResult;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-[24px] font-extralight text-gray-900 text-balance" style={{ letterSpacing: "-0.4px" }}>
          {isGlp1Recovery
            ? "We hear you — this study may be a fit"
            : "You may qualify"}
        </h1>
        <p className="text-[14px] text-gray-600 text-pretty">
          {isGlp1Recovery
            ? "You've already taken steps to treat this medically. The VESPER 5 study is designed for adults like you. Based on your responses, you may be eligible to learn more."
            : "Based on your responses, you may be eligible for this research study."}
        </p>
      </div>

      {prescreenResult?.tier && (
        <div className="bg-[var(--color-status-success-bg)] rounded-xl p-4 text-[13px] text-[var(--color-status-success-text)]">
          Screening tier: <strong>Tier {prescreenResult.tier}</strong>
        </div>
      )}

      {error && (
        <div className="bg-[var(--color-status-danger-bg)] rounded-xl p-3 text-[13px] text-[var(--color-status-danger-text)]" role="alert">
          {error}
        </div>
      )}

      {!done ? (
        <div className="bg-white rounded-2xl p-6 space-y-5" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
          <div className="space-y-2">
            <label htmlFor="email" className="block text-[13px] font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)]"
              placeholder="you@example.com"
              aria-required="true"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="phone" className="block text-[13px] font-medium text-gray-700">
              Phone number (optional)
            </label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)]"
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="zip" className="block text-[13px] font-medium text-gray-700">
              ZIP code (optional)
            </label>
            <input
              id="zip"
              type="text"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-[var(--color-leap21-border-input)] text-[14px] focus:outline-none focus:ring-2 focus:ring-[var(--color-leap21-focus)]"
              placeholder="27513"
            />
          </div>

          <CallbackPreferenceSection
            detectedTimezone={detectedTimezone}
            timezoneOptions={timezoneOptions}
            callbackWindow={callbackWindow}
            onCallbackWindowChange={setCallbackWindow}
            timezone={timezone}
            onTimezoneChange={setTimezone}
            timezoneConfirmed={timezoneConfirmed}
            onTimezoneConfirmChange={(checked) =>
              setConfirmedTimezone(checked ? timezone : null)
            }
            tcpaConsent={tcpaConsent}
            onTcpaConsentChange={setTcpaConsent}
          />

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={optIn}
              onChange={(e) => setOptIn(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border-gray-300"
            />
            <span className="text-[13px] text-gray-600">
              Yes, I would like to be contacted about future research studies I may qualify for.
            </span>
          </label>

          <button
            onClick={handleSubmit}
            disabled={loading || !callbackComplete}
            className="w-full py-3 rounded-xl bg-gray-900 text-white text-[14px] font-medium hover:bg-gray-800 active:scale-[0.97] transition-[transform,opacity,background-color] duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Submitting..." : callbackComplete ? "Submit and Finish" : "Complete callback preference to submit"}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-6 space-y-4" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
          <div className="text-center space-y-2">
            <div className="w-12 h-12 rounded-full bg-[var(--color-status-success-bg)] text-[var(--color-status-success-text)] flex items-center justify-center mx-auto text-[20px]">
              ✓
            </div>
            <h2 className="text-[18px] font-medium text-gray-900">Thank you!</h2>
            <p className="text-[14px] text-gray-600">
              Your information has been saved. A study coordinator will contact you soon.
            </p>
          </div>

          {completeResult?.siteAssignment && (
            <div className="bg-[var(--color-leap21-brand-light)] rounded-xl p-4 text-center">
              <p className="text-[13px] text-[var(--color-leap21-brand)] font-medium">
                Assigned site: {completeResult.siteAssignment.name}
              </p>
              <p className="text-[12px] text-gray-500">
                {completeResult.siteAssignment.city}, {completeResult.siteAssignment.state}
              </p>
            </div>
          )}

          <button
            onClick={() => navigate("/apply/vesper5/welcome")}
            className="w-full py-3 rounded-xl border border-gray-200 text-gray-900 text-[14px] font-medium hover:bg-gray-50 active:scale-[0.97] transition-[transform,background-color] duration-200"
          >
            Return to home
          </button>
        </div>
      )}
    </div>
  );
}
